// Route: /voice-tutor — список голосовых сессий репетитора + кнопка «Новая
// сессия» (→ /voice-tutor/new, форма создания). Отдельный раздел (вне /tutor,
// чтобы не смешиваться с чатами). Единая база списков (EntityItem/ListToolbar).

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import { toast } from "@studyhub/ui/toast";
import { IconPlus, IconTrash, IconWaveSine } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import {
	EntityItem,
	LIST_PAGE_GRID,
	LIST_PAGE_SHELL,
	LIST_PAGE_STACK,
	ListToolbar,
} from "@/components/shared/entity-list";
import { EntityTimestamp } from "@/components/shared/entity-timestamp";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import { useConfirm } from "@/hooks/use-confirm";
import { useRefetchOnOnline } from "@/hooks/use-refetch-on-online";
import { useRequireOnline } from "@/hooks/use-require-online";
import { useViewMode } from "@/hooks/use-view-mode";
import { cn } from "@/lib/cn";
import { formatDuration } from "@/lib/format";
import { api } from "@/lib/trpc-client";

/** Тактильная клавиша primary-действия на карточке (как «Слушать» в аудио-резюме). */
const TACTILE_KEY_CLASS = cn(
	"sh-tactile-key inline-flex h-9 items-center justify-center gap-1.5 rounded-[var(--radius-lg)] pointer-coarse:min-h-11",
	"px-3.5 text-sm font-medium text-[var(--sh-primary-fg)]",
	"[background-image:linear-gradient(180deg,var(--primary-2),var(--primary))] cursor-pointer",
);

const TILTS = [-1.2, 0.8, -0.6, 1, -0.9, 0.6] as const;

const searchSchema = z.object({ q: z.string().optional() });

export const Route = createFileRoute("/voice-tutor/")({
	validateSearch: tolerantSearch(searchSchema),
	component: VoiceTutorListPage,
	pendingComponent: PagePendingFallback,
});

function VoiceTutorListPage(): React.ReactElement {
	const { t } = useTranslation("voice");
	const { t: tTrash } = useTranslation("trash");
	const intro = useSectionIntro("voice-tutor");
	const navigate = useNavigate({ from: "/voice-tutor/" });
	const search = Route.useSearch();
	const [view, setView] = useViewMode("voice-tutor");
	const confirm = useConfirm();
	const { online, guard } = useRequireOnline();

	const query = api.voiceTutor.sessions.list.useQuery({ limit: 50 });
	useRefetchOnOnline(() => query.refetch(), query.isError);
	const deleteSession = api.voiceTutor.sessions.delete.useMutation({
		onSuccess: () => {
			toast.success(tTrash("cascade.ttl"));
			void query.refetch();
		},
		onError: () => toast.error(tTrash("actionFailed")),
	});
	const all = useMemo(() => query.data?.items ?? [], [query.data]);

	async function handleDelete(sessionId: string, topic: string) {
		if (!guard()) return;
		const ok = await confirm({
			title: tTrash("cascade.title", { title: topic }),
			description: tTrash("cascade.leaf"),
			confirmLabel: tTrash("cascade.confirm"),
			tone: "destructive",
		});
		if (!ok) return;
		deleteSession.mutate({ voiceSessionId: sessionId });
	}

	const items = useMemo(() => {
		if (!search.q) return all;
		const q = search.q.toLowerCase();
		return all.filter((s) => s.topic.toLowerCase().includes(q));
	}, [all, search.q]);

	const setQuery = (value: string): void =>
		void navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });

	const containerClass = view === "grid" ? LIST_PAGE_GRID : LIST_PAGE_STACK;

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				category="ai"
				tint={SECTION_TINT.voiceTutor}
				icon={<IconWaveSine />}
				title={t("title")}
				description={t("list.subtitle", {
					defaultValue: "Голосовые сессии",
					count: all.length,
				})}
				// Ссылка-возврат плюс кнопка — уже широкий блок: без переноса он
				// съедал бы заголовок на 360px (проверено скриншотом песочницы).
				stackActions
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button
							className="cursor-pointer"
							disabled={!online}
							onClick={() => {
								if (!guard()) return;
								void navigate({ to: "/voice-tutor/new" });
							}}
						>
							<IconPlus size={16} />
							<span className="ml-1.5 hidden sm:inline">
								{t("list.newSession", { defaultValue: "Новая сессия" })}
							</span>
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля,
			    состояние «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{(all.length > 0 || search.q) && !query.isError && (
				<ListToolbar
					search={{
						value: search.q ?? "",
						onChange: setQuery,
						placeholder: t("list.searchPlaceholder", { defaultValue: "Поиск по сессиям…" }),
					}}
					view={view}
					onViewChange={setView}
				/>
			)}

			{query.isLoading ? (
				<div className={containerClass} aria-busy="true">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={i} className="h-[72px] rounded-[var(--radius-card)]" />
					))}
				</div>
			) : query.isError ? (
				<ErrorState
					title={t("list.errorTitle", { defaultValue: "Не удалось загрузить сессии" })}
					action={
						<Button
							variant="outline"
							onClick={() => void query.refetch()}
							className="cursor-pointer"
						>
							{t("list.retry", { defaultValue: "Повторить" })}
						</Button>
					}
				/>
			) : items.length === 0 ? (
				<EmptyState
					icon={<IconWaveSine />}
					title={t("recentSessions.empty", { defaultValue: "Пока нет сессий" })}
					description={t("list.emptyDescription", {
						defaultValue: "Начни голосовую сессию — репетитор знает твой материал.",
					})}
					action={
						<Button
							className="cursor-pointer"
							disabled={!online}
							onClick={() => {
								if (!guard()) return;
								void navigate({ to: "/voice-tutor/new" });
							}}
						>
							<IconPlus size={16} className="mr-1" />
							{t("list.newSession", { defaultValue: "Новая сессия" })}
						</Button>
					}
				/>
			) : (
				<div className={containerClass}>
					{items.map((s, i) => {
						// «Этап X из Y» — где остановились (currentSectionIndex 0-based → 1-based),
						// прогресс-бар как в учебном плане. Показываем только при наличии плана.
						const stage =
							s.totalSections > 0 ? Math.min(s.currentSectionIndex + 1, s.totalSections) : 0;
						const progress =
							s.totalSections > 0 ? Math.round((stage / s.totalSections) * 100) : undefined;
						// «Продолжить» показываем у живых/паузнутых сессий И у завершённых
						// с непройденным до конца планом урока: явный клик с карточки →
						// ?resume=true → детальная страница монтирует Live и возобновляет
						// через sessions.resume (она же защищена от «выброса» при мгновенном
						// повторном завершении — onEnded показывает read-only историю).
						// failed и полностью пройденные планы — только история по клику.
						const isTerminal = s.status === "ended" || s.status === "failed";
						const lessonIncomplete = s.totalSections > 0 && s.completedCount < s.totalSections;
						const canContinue = !isTerminal || (s.status === "ended" && lessonIncomplete);
						return (
							<EntityItem
								key={s.id}
								view={view}
								tile={{ category: "ai", icon: <IconWaveSine /> }}
								title={s.topic}
								to={{ to: "/voice-tutor/session/$id", params: { id: s.id } }}
								tilt={view === "grid" ? TILTS[i % TILTS.length] : undefined}
								progress={progress}
								progressTone="success"
								primaryAction={
									canContinue ? (
										<button
											type="button"
											onClick={(e) => {
												e.stopPropagation();
												// Явное возобновление: живой/паузнутой — продолжить,
												// завершённой с незаконченным планом — оживить с того же места.
												void navigate({
													to: "/voice-tutor/session/$id",
													params: { id: s.id },
													search: { resume: true },
												});
											}}
											className={TACTILE_KEY_CLASS}
										>
											<IconWaveSine size={14} />
											{t("list.continue", { defaultValue: "Продолжить" })}
										</button>
									) : undefined
								}
								menuItems={[
									{
										key: "delete",
										label: tTrash("cascade.confirm"),
										icon: <IconTrash />,
										tone: "destructive",
										disabled: deleteSession.isPending || !online,
										onSelect: () => void handleDelete(s.id, s.topic),
									},
								]}
								swipeRight={[
									{
										key: "delete",
										label: tTrash("cascade.confirm"),
										icon: <IconTrash />,
										tone: "destructive",
										onAction: () => void handleDelete(s.id, s.topic),
									},
								]}
								meta={
									<>
										<span className="num inline-flex items-baseline gap-1 tabular-nums">
											{s.durationSec > 0 ? (
												<>
													<span>{formatDuration(s.durationSec)}</span>
													<span aria-hidden>·</span>
												</>
											) : null}
											<EntityTimestamp value={s.createdAt} />
										</span>
										{stage > 0 && (
											<span className="num tabular-nums">
												{t("list.stageProgress", {
													defaultValue: "Этап {{stage}} из {{total}}",
													stage,
													total: s.totalSections,
												})}
											</span>
										)}
									</>
								}
							/>
						);
					})}
				</div>
			)}
		</div>
	);
}
